/**
 * What a reader sees when a screen THROWS.
 *
 * Until this existed, nothing in the app had an `errorElement`, so React Router fell back to its
 * own development screen: the words "Unexpected Application Error!", the exception's message, a
 * JavaScript stack trace of minified bundle offsets, and a paragraph beginning "Hey developer 👋".
 * The founder photographed exactly that, in production, on All Members — a real crash
 * (`member.workspaces.slice` on a field an older Worker omits) turned into a page that told an
 * administrator nothing, offered no way back, and addressed them as the person who wrote it.
 *
 * The crash itself is fixed at the API boundary (`api/workspaces.ts`). This is the other half, and
 * the more important one: it is what stops the NEXT unguarded field from taking the whole console
 * down. A boundary cannot prevent bugs; it decides what a bug costs.
 *
 * ## Two boundaries, not one
 *
 * `routes/index.tsx` mounts this at `/app` and again at `/app/admin`. React Router walks UP to the
 * nearest `errorElement`, so the inner one keeps the admin nav on screen while one panel fails —
 * the reader can go somewhere else without a page load. The outer one catches everything else, and
 * it is what the shell falls back to if the shell's own children throw.
 *
 * ## What it says and what it deliberately does not
 *
 * It never shows the exception's message. A thrown `TypeError`'s text is written for whoever reads
 * the source, and "Cannot read properties of undefined (reading 'slice')" tells an administrator
 * nothing they can act on while strongly implying their data is damaged. What it does say is the
 * one fact that is both true and load-bearing: nothing was changed, because a render throw happens
 * after the read and before any write.
 *
 * The message IS written to the console, once, where a developer looking for it will find it.
 *
 * ## One of these is not a bug at all, and it was being reported as one
 *
 * Every screen in the console is a lazy route (`chunked(...)` in `routes/index.tsx`), and the
 * editor loads two more chunks of its own. So a tab left open across a deploy asks for a hashed
 * asset that the new build no longer has: Cloudflare Pages answers 404, the dynamic import
 * rejects, and React Router hands the rejection here exactly as though a component had thrown.
 *
 * The screen then told an administrator that something on it "failed while it was being drawn",
 * which is false — nothing on the screen ran, because the screen never arrived — and offered a
 * reload as a suggestion rather than doing it. Pages builds on every push (CLAUDE.md §8), so this
 * is not a rare condition; it is the ordinary cost of shipping while somebody has the console open.
 *
 * A stale chunk is recoverable with certainty, by exactly the thing the reader would have done, so
 * this reloads ONCE by itself and says what happened only if that did not work. Once, because a
 * reload that does not fix it must not loop: the attempt is stamped in `sessionStorage` and a
 * second failure inside the cooldown falls through to the message. A browser that will not store
 * never auto-reloads at all, for the same reason.
 */
import { useEffect, useState } from 'react'
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { Button, Card, ErrorState } from '@flowza/ui'
import { claimReload, isStaleChunkError as isStale } from '../stale-chunk'

/**
 * Both helpers now live in `src/stale-chunk.ts`, because the SIGNING CEREMONY needs them too and
 * it is a separate entry point with no router. Re-exported here so the existing tests and any
 * caller that learned this path keep working.
 */
export { isStaleChunkError } from '../stale-chunk'

export function RouteError({ scope }: { readonly scope: 'app' | 'admin' }) {
  const error = useRouteError()

  // The one place the real error goes. `console.error` is allowed by eslint's `no-console` config
  // precisely for this; swallowing it entirely would make a production crash unreportable.
  console.error('A route failed to render', error)

  // A 404 from the router (an unmatched nested path) is a different thing from a crash, and saying
  // "this screen failed" about a mistyped URL would send somebody looking for a bug that is not
  // there.
  const missing = isRouteErrorResponse(error) && error.status === 404
  const stale = !missing && isStale(error)

  // In an EFFECT, never in the render: `claimReload` writes, and a component that wrote to storage
  // while drawing would do it again on every re-render React chose to make.
  const [recovering, setRecovering] = useState(false)
  useEffect(() => {
    if (!stale || !claimReload(Date.now())) return
    setRecovering(true)
    window.location.reload()
  }, [stale])

  const title = missing
    ? 'That page does not exist'
    : recovering
      ? 'Getting the latest version…'
      : stale
        ? 'This tab is running an older version'
        : 'This screen did not load'

  const body = missing
    ? 'The address may be mistyped, or the page may have moved. Everything else in the console is still here.'
    : recovering
      ? 'FlowZa Sign was updated while this tab was open, so part of this screen was no longer where it was. Reloading now — nothing you did was lost.'
      : stale
        ? 'FlowZa Sign was updated while this tab was open, so the screen you asked for was no longer where this tab expected it. Nothing was changed, and reloading picks up the new version.'
        : 'Something on this screen failed while it was being drawn. Nothing was changed — a screen fails before it writes anything. Reloading usually clears it; if it does not, the rest of the console still works.'

  return (
    <div className="mx-auto max-w-2xl py-8">
      <Card>
        <ErrorState title={title} body={body} />
        <div className="mt-4 flex flex-wrap gap-2">
          {/*
            A real reload, not a router navigation: the component that threw is still mounted in
            React's tree, and routing back to the same place re-renders the same broken state. This
            is the one control that reliably works after a render throw.
          */}
          <Button onClick={() => window.location.reload()}>Reload this page</Button>
          <Link
            to={scope === 'admin' ? '/app/admin' : '/app'}
            className="border-border-strong text-text-primary hover:bg-surface-sunken h-control inline-flex items-center rounded-md border px-3 text-sm font-medium no-underline"
          >
            {scope === 'admin' ? 'Back to Admin' : 'Back to the dashboard'}
          </Link>
        </div>
      </Card>
    </div>
  )
}
